/**
 * The team this tab's requests act on: the active team (X-Team-Id) and, for
 * staff, the impersonated team (X-Impersonate-Team).
 *
 * Each tab keeps its own copy in sessionStorage, which survives the reload a
 * team switch or an impersonation change ends with. One shared localStorage
 * value used to be the only copy, so a change in one tab re-scoped every
 * other open tab's next request while those tabs still showed the old team's
 * cached data. Opening a team's Configure page, which ends impersonation,
 * took every other tab out of impersonation too: their banner vanished at
 * the next token renew, the customer's data stayed on screen, and the next
 * click into it 404ed.
 *
 * localStorage now only seeds new tabs. A tab writes its scope there while it
 * has focus, so a link opened in a new tab starts in the scope of the tab it
 * was opened from, and a tab loading in the background never changes what
 * the next new tab starts with.
 */

export const ACTIVE_TEAM_KEY = 'platform-v2-active-team'
export const IMPERSONATE_TEAM_KEY = 'platform-v2-impersonate-team'

type TabScopeKey = typeof ACTIVE_TEAM_KEY | typeof IMPERSONATE_TEAM_KEY

const TAB_SCOPE_KEYS: readonly TabScopeKey[] = [ACTIVE_TEAM_KEY, IMPERSONATE_TEAM_KEY]

/**
 * This tab's copy when it holds no team, which is how a tab that is not
 * impersonating stays that way across a reload while the seed says
 * otherwise. Team ids are never empty.
 */
const NO_TEAM = ''

/** This tab's team for `key`. A new tab's first read adopts the seed and keeps it. */
export function readTabScope(key: TabScopeKey): string | null {
  const own = sessionStorage.getItem(key)
  if (own !== null) return own === NO_TEAM ? null : own
  const seeded = localStorage.getItem(key)
  sessionStorage.setItem(key, seeded ?? NO_TEAM)
  return seeded
}

/** Set this tab's team for `key`, and the seed too when this tab has focus. */
export function writeTabScope(key: TabScopeKey, teamId: string | null): void {
  sessionStorage.setItem(key, teamId ?? NO_TEAM)
  if (document.hasFocus()) writeSeed(key, teamId)
}

/** Make this tab's scope the one new tabs start from, if this tab has focus. */
export function seedNewTabs(): void {
  if (!document.hasFocus()) return
  for (const key of TAB_SCOPE_KEYS) writeSeed(key, readTabScope(key))
}

function writeSeed(key: TabScopeKey, teamId: string | null): void {
  if (teamId === null) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, teamId)
}
